import { AlertTriangle, FileUp, FlaskConical, Inbox, Loader2, Radio, Sparkles } from "lucide-react";
import { humanize } from "@/lib/api";

export const PageHeader = ({ eyebrow, title, description, actions, testId = "page-header" }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 cq-rise" data-testid={testId}>
    <div className="max-w-2xl">
      {eyebrow && <div className="cq-label mb-3">{eyebrow}</div>}
      <h1 className="text-3xl sm:text-4xl font-semibold text-[#F7FAFF]">{title}</h1>
      {description && <p className="mt-2 text-sm text-[#AAB6CC] leading-relaxed">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const DemoBadge = ({ label = "Demonstration data", testId = "demo-data-badge" }) => (
  <span data-testid={testId} className="cq-chip text-[#F4B860] border-[#F4B860]/30 bg-[#F4B860]/10">
    <FlaskConical size={12} /> {label}
  </span>
);

// Data provenance labels: live connector, manual import, demonstration and AI output are always shown distinctly.
const SOURCES = {
  live: ["text-[#4BE3A4] border-[#4BE3A4]/30 bg-[#4BE3A4]/10", Radio, "Live connected data"],
  imported: ["text-[#22E6F2] border-[#22E6F2]/30 bg-[#22E6F2]/10", FileUp, "Manually imported"],
  demo: ["text-[#F4B860] border-[#F4B860]/30 bg-[#F4B860]/10", FlaskConical, "Demonstration data"],
  ai: ["text-[#B89CFF] border-[#855BFF]/40 bg-[#855BFF]/10", Sparkles, "AI recommendation"],
};
export const SourceBadge = ({ source, label, testId = "data-source-badge" }) => {
  if (!SOURCES[source]) return null;
  const [cls, Icon, text] = SOURCES[source];
  return <span data-testid={testId} className={`cq-chip ${cls}`}><Icon size={12} /> {label || text}</span>;
};

const TONES = {
  green: "text-[#4BE3A4] border-[#4BE3A4]/30 bg-[#4BE3A4]/10",
  amber: "text-[#F4B860] border-[#F4B860]/30 bg-[#F4B860]/10",
  red: "text-[#FF7792] border-[#FF7792]/30 bg-[#FF7792]/10",
  cyan: "text-[#22E6F2] border-[#22E6F2]/30 bg-[#22E6F2]/10",
  violet: "text-[#B89CFF] border-[#855BFF]/40 bg-[#855BFF]/10",
  slate: "text-[#AAB6CC] border-[#243153] bg-[#131F42]",
};

const STATUS_TONE = {
  approved: "green", strategy_approved: "green", active: "green", healthy: "green", accepted: "green", done: "green",
  pending: "amber", pending_approval: "amber", in_review: "amber", awaiting_approval: "amber", attention: "amber", snoozed: "violet",
  changes_requested: "violet", rejected: "red", cancelled: "red", dismissed: "slate", draft: "cyan", interpreted: "cyan",
  idea: "slate", planned: "slate", not_connected: "slate", new: "cyan", scheduled: "cyan", paused: "slate", retired: "slate",
  drafted: "cyan", published_manual: "green", revoked: "slate", expired: "red", superseded: "slate", ready: "green", generating: "violet",
};

export const Chip = ({ tone = "slate", children, testId }) => (
  <span data-testid={testId} className={`cq-chip ${TONES[tone]}`}>{children}</span>
);

export const fmtWait = (h) => (h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h`);

export const WaitBadge = ({ a, testId }) => {
  if (!a?.due_at) return null;
  const open = ["pending", "snoozed", "changes_requested"].includes(a.status);
  const left = Math.round((new Date(a.due_at) - Date.now()) / 3600000);
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 text-[11px] font-mono">
      <span className="text-[#7C8BAD]">{open ? "Waiting" : a.status === "cancelled" ? "Cancelled after" : "Took"} {fmtWait(a.waiting_hours)}</span>
      {open && (a.overdue ? <Chip tone="red">Overdue</Chip> : a.status === "snoozed" ? null : <span className={left <= 12 ? "text-[#F4B860]" : "text-[#7C8BAD]"}>· due in {fmtWait(Math.max(left, 0))}</span>)}
    </span>
  );
};

const STATUS_LABEL = { published_manual: "Manually published" };
export const StatusBadge = ({ status, testId }) => (
  <Chip tone={STATUS_TONE[status] || "slate"} testId={testId}>{STATUS_LABEL[status] || humanize(status)}</Chip>
);

const RISK_TONE = { low: "green", medium: "amber", high: "red", critical: "red" };
export const RiskBadge = ({ level, testId }) => (
  <Chip tone={RISK_TONE[level] || "slate"} testId={testId}>
    {level === "critical" && <AlertTriangle size={11} />} Risk · {humanize(level)}
  </Chip>
);

export const ConfidenceBadge = ({ level, testId }) => {
  const bars = { low: 1, medium: 2, high: 3 }[level] || 0;
  return (
    <span data-testid={testId} className="inline-flex items-center gap-2 text-[11.5px] font-semibold text-[#AAB6CC]">
      <span className="flex items-end gap-[2px]">
        {[1, 2, 3].map((i) => (
          <span key={i} className="w-[3px] rounded-sm" style={{ height: 4 + i * 3, background: i <= bars ? "#22E6F2" : "#243153" }} />
        ))}
      </span>
      {humanize(level)} confidence
    </span>
  );
};

export const EmptyState = ({ title, description, action, icon: Icon = Inbox, testId = "empty-state" }) => (
  <div data-testid={testId} className="flex flex-col items-center justify-center text-center py-14 px-6 border border-dashed border-[#243153] rounded-xl">
    <div className="w-11 h-11 rounded-xl bg-[#131F42] border border-[#243153] flex items-center justify-center mb-4">
      <Icon size={18} className="text-[#22E6F2]" />
    </div>
    <div className="font-display text-base font-medium text-[#F7FAFF]">{title}</div>
    {description && <p className="mt-1 text-sm text-[#AAB6CC] max-w-sm">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Loading = ({ label = "Loading", testId = "loading-state" }) => (
  <div data-testid={testId} className="flex items-center gap-3 text-sm text-[#AAB6CC] py-12 justify-center">
    <Loader2 size={16} className="animate-spin text-[#22E6F2]" /> {label}…
  </div>
);

export const ErrorState = ({ message, onRetry, testId = "error-state" }) => (
  <div data-testid={testId} className="flex items-start gap-3 p-4 rounded-xl border border-[#FF7792]/30 bg-[#FF7792]/5 text-sm">
    <AlertTriangle size={16} className="text-[#FF7792] mt-0.5 shrink-0" />
    <div className="flex-1 text-[#F7FAFF]">{message || "Something went wrong."}</div>
    {onRetry && <button data-testid="error-retry-button" onClick={onRetry} className="cq-btn cq-btn-ghost !py-1 !px-3">Retry</button>}
  </div>
);

export const QueryState = ({ q, children, empty, loadingLabel }) => {
  if (q.isLoading) return <Loading label={loadingLabel} />;
  if (q.error) return <ErrorState message={q.error?.response?.data?.detail || "Could not load data."} onRetry={q.refetch} />;
  if (empty && (!q.data || (Array.isArray(q.data) && !q.data.length))) return empty;
  return children(q.data);
};

export const Panel = ({ title, eyebrow, action, children, className = "", testId }) => (
  <section data-testid={testId} className={`cq-card p-5 sm:p-6 ${className}`}>
    {(title || action) && (
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          {eyebrow && <div className="cq-muted-label mb-1.5">{eyebrow}</div>}
          {title && <h3 className="font-display text-[17px] font-medium text-[#F7FAFF]">{title}</h3>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="cq-muted-label block mb-1.5">{label}</span>
    {children}
    {hint && <span className="block mt-1 text-xs text-[#7C8BAD]">{hint}</span>}
  </label>
);

export const ListInput = ({ value = [], onChange, rows = 3, placeholder = "One item per line", testId }) => (
  <textarea
    data-testid={testId}
    className="cq-input font-normal"
    rows={rows}
    placeholder={placeholder}
    value={(value || []).join("\n")}
    onChange={(e) => onChange(e.target.value.split("\n"))}
    onBlur={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
  />
);
